import store from '@/store'
import { joinWithBr } from '@/utils/html'
import { getMainMetadata, buildDescription } from '@/components/combined-predict/models/metadata'
import { applyDefaultChildrenIfNoChildren } from '@/components/combined-predict/components/CombinedPredictTable/utils/mappedGroupBuilders'
import { get, isNil } from 'lodash-es'
import { modelStateRegistry } from '@/utils/modelStateRegistry'
import { getRomanNumeral } from '@/utils/getRomanNumeral'

const getOperaMainMetadata = () => {
  const keys = get(modelStateRegistry, 'opera.keys')
  const mainMetadata = store.getters[`opera/${keys.getVersion}`] || null

  const name = mainMetadata?.title

  const compiledAt = mainMetadata?.compiled
  const description = mainMetadata?.description
  const version = mainMetadata?.version
  const buildId = mainMetadata?.build_id
  return getMainMetadata({ name, compiledAt, description, version, buildId })
}

const getOperaName = () => getOperaMainMetadata()?.name ?? 'OPERA'

const getOperaDescription = () => buildDescription(getOperaMainMetadata())

const hazardCategoriesThresholds = {
  CATMoS_EPA: {
    unit: 'mg/kg',
    thresholds: [
      { category: 1, max: 50 },
      { category: 2, min: 50, max: 500 },
      { category: 3, min: 500, max: 5000 },
      { category: 4, min: 5000 },
    ],
  },
  CATMoS_GHS: {
    unit: 'mg/kg',
    thresholds: [
      { category: 1, max: 5 },
      { category: 2, min: 5, max: 50 },
      { category: 3, min: 50, max: 300 },
      { category: 4, min: 300, max: 2000 },
      { category: 5, min: 2000 },
    ],
  },
}

const findThresholdByCategory = (category, endpointId) => {
  const categoryThresholds = get(hazardCategoriesThresholds, endpointId)?.thresholds || []
  return categoryThresholds.find((threshold) => threshold.category === category)
}

const getCategoryLabelByValue = (category) => {
  return getRomanNumeral(category)
}

const getCategoryThresholdText = (threshold, unit) => {
  const label = getCategoryLabelByValue(threshold.category)
  const min = threshold.min
  const max = threshold.max

  const getThresholdRangeText = (min, max) => {
    if (!isNil(min) && !isNil(max)) return `> ${min} ≤ ${max} ${unit}`
    if (!isNil(min)) return `> ${min} ${unit}`
    return `≤ ${max} ${unit}`
  }
  return `${label ? `${label}: ` : ''}${getThresholdRangeText(min, max)}`
}

const getCategoryTextByCategory = (category, endpointId) => {
  const threshold = findThresholdByCategory(category, endpointId)
  if (!threshold) return null
  const unit = get(hazardCategoriesThresholds, endpointId)?.unit || ''
  return getCategoryThresholdText(threshold, unit)
}

const categoricalEndpoints = ['CATMoS_EPA', 'CATMoS_GHS']

const getDescription = (description, endpointId) => {
  const richCategoryDescription = (description) => {
    const category = get(hazardCategoriesThresholds, endpointId)
    const unit = category?.unit
    const thresholds = category?.thresholds || []
    const categoryDescriptions = thresholds.map((threshold) => getCategoryThresholdText(threshold, unit))
    return joinWithBr([description, ...categoryDescriptions])
  }
  if (categoricalEndpoints.includes(endpointId)) return richCategoryDescription(description)
  return description
}

const getOperaColumnGroups = () => {
  const keys = get(modelStateRegistry, 'opera.keys')
  const endpoints = store.getters[`opera/${keys.getEndpoints}`] || []

  if (!Array.isArray(endpoints)) return []

  return endpoints.map((endpoint) =>
    applyDefaultChildrenIfNoChildren({
      id: endpoint?.id,
      name: endpoint?.name || endpoint?.id,
      unit: endpoint?.units,
      description: getDescription(endpoint?.description, endpoint?.id),
    }),
  )
}

export {
  getOperaColumnGroups,
  getOperaDescription,
  getOperaName,
  categoricalEndpoints,
  getCategoryLabelByValue,
  getCategoryTextByCategory,
}
